
import { CreatureFeature } from "@models";

import { 
  AbilityEffect, 
  BasicCreatureInstance, 
  Potence, 
  RollPlus, 
  Spell,
  SummonInstance,
  UpgradableNumber,
  TransformInstance,
  ConsumedResource,
  ComplexCreatureInstance
} from "@dndModels";
import { AbilityScoreExpandedType, AttackType, CastingTime, DurationType, EffectType, SpellLevel } from "@dndModels/Types";
import { STANDARD_CASTING_TIMES } from "@dndModels/Constants";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";

const char_util = DandDCharacterUtilities.getInstance();

/**
 * It's CreatureAbility2.ts because of a git issue.
 */
export class CreatureAbility extends CreatureFeature {
  saving_throw_ability_score: AbilityScoreExpandedType | "" = ""; // Ability Score saving throw the target(s) have to make
  effects: AbilityEffect[] = []; // Formula for how much damage/healing to do
  // effect_2: AbilityEffect; // Some abilities have a second (like Ice Knife or Booming Blade or things that do different types of damage)
  range = ""; // Self, Touch, or a number
  range_2 = ""; // For some there are multiple ranges.  It can be an AoE size, or sometimes something else.
  concentration = false;
  notes = "";
  duration: DurationType = "Instantaneous";
  components: string[] = []; // VSM
  material_component = "";
  casting_time: CastingTime = "A"; // A, BA, RA, X minute(s), etc
  special_casting_time = "";

  consumed_resource: ConsumedResource = new ConsumedResource();
  // resource_consumed: string = ""; // Special or Legendary Action
  // amount_consumed: number = 0;
  // special_resource_amount: number = 0;
  // special_resource_refresh_rule: string = ""; // Short Rest, Long Rest, Dawn, 1 Hour, 8 Hours, 24 Hours
  // recharge_die: 4 | 6 | 8 | 10 | 12 | 20 | 100 = 6;
  // recharge_on: number[] = [];

  attack_bonus: UpgradableNumber = new UpgradableNumber();
  dc: UpgradableNumber = new UpgradableNumber();
  damage_string = "";
  rolls: RollPlus[] = [];
  
  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.saving_throw_ability_score = obj.saving_throw_ability_score ? obj.saving_throw_ability_score : "";
      if (obj.effects && obj.effects.length) {
        for (const effect of obj.effects) {
          this.effects.push(new AbilityEffect(effect)); // , this.name === "Proboscis"));
        }
        // if (this.name === "Proboscis") {
        //   // debugger;
        //   console.log(this.effects.map(o => o.clone()));
        // }
      } else if (obj.effect) {
        this.effects.push(new AbilityEffect(obj.effect));
      }
      this.range = obj.range ? obj.range : "";
      this.range_2 = obj.range_2 ? obj.range_2 : "";
      this.concentration = obj.concentration ? obj.concentration : false;
      this.notes = obj.notes ? obj.notes : "";
      this.duration = obj.duration ? obj.duration : "Instantaneous";
      this.components = obj.components ? [...obj.components] : [];
      this.material_component = obj.material_component ? obj.material_component : "";
      this.casting_time = obj.casting_time ? obj.casting_time : "A";
      
      this.consumed_resource = obj.consumed_resource ? new ConsumedResource(obj.consumed_resource) : new ConsumedResource();
      if (!obj.consumed_resource) {
        this.consumed_resource.resource_consumed = obj.resource_consumed ? obj.resource_consumed : "None";
        this.consumed_resource.amount_consumed = obj.amount_consumed ? +obj.amount_consumed : 0;
        this.consumed_resource.special_resource_amount = obj.special_resource_amount ? new UpgradableNumber(obj.special_resource_amount) : new UpgradableNumber();
        this.consumed_resource.special_resource_refresh_rule = obj.special_resource_refresh_rule ? obj.special_resource_refresh_rule : "";
      }

      this.attack_bonus = obj.attack_bonus ? new UpgradableNumber(obj.attack_bonus) : new UpgradableNumber();
      this.dc = obj.dc ? new UpgradableNumber(obj.dc) : new UpgradableNumber();
      
        if (this.saving_throw_ability_score !== "") {
        for (const effect of this.effects.filter(o => o.attack_type === "Save" && o.saving_throw_ability_score === "")) {
          effect.saving_throw_ability_score = this.saving_throw_ability_score;
        }
      }
      const desc_lower = this.description.toLowerCase();
      if (desc_lower.includes("melee") && this.effects.length > 0 && this.effects.filter(o => o.attack_type !== "None").length === 0) {
        // Melee Weapon Attack: +14 to hit, reach 10 ft., one target. Hit: 19 (2d10 + 8) piercing damage.
        const effect = this.effects[0];
        effect.attack_type = desc_lower.includes("spell") ? "Melee Spell" : "Melee";
        if (desc_lower.toLowerCase().includes(" reach ")) {
          let pieces = desc_lower.split(" reach ");
          pieces = pieces[1].split(" ");
          if (!isNaN(+pieces[0])) {
            effect.target_int.range.base = +pieces[0];
          }
        }
      } else if (desc_lower.includes("ranged weapon") && this.effects.length > 0 && (this.effects.filter(o => o.attack_type !== "None").length === 0 || this.effects.filter(o => o.attack_type !== "Ranged" && o.target_int.range.isBlank()).length === 0)) {
        // Ranged Weapon Attack: +{feature_source.non_magic_attack} to hit, range 80 ft./320 ft., one target. Hit: 5 (1d8 + {feature_source.non_magic_dmg_bonus}) piercing damage.
        // Ranged Weapon Attack: +4 to hit, range 150/600 ft., one target. Hit: 6 (1d8 + 2) piercing damage.
        // debugger;
        const effect = this.effects[0];
        effect.attack_type = "Ranged";
        if (desc_lower.toLowerCase().includes(" range ")) {
          let pieces = desc_lower.split(" range ");
          pieces = pieces[1].split(" ");
          if (pieces[0].includes("/")) {
            pieces = pieces[0].split("/");
            if (!isNaN(+pieces[0])) {
              effect.target_int.range.base = +pieces[0];
            }
            if (!isNaN(+pieces[1])) {
              effect.target_int.range2.base = +pieces[1];
            }
          } else {
            if (!isNaN(+pieces[0])) {
              effect.target_int.range.base = +pieces[0];
            }
            if (pieces[1].includes("/")) {
              pieces = pieces[1].split("/");
              if (!isNaN(+pieces[1])) {
                effect.target_int.range2.base = +pieces[1];
              }
            }
          }
        }
      }
      this.special_casting_time = obj.special_casting_time ? obj.special_casting_time : "";
      if (!STANDARD_CASTING_TIMES.includes(this.casting_time)) {
        this.special_casting_time = this.casting_time;
        this.casting_time = "Special";
      }
      // if (obj.name && obj.name.includes("Tadpole")) {
      //   debugger;
      // }
      if (!obj.consumed_resource) {
        this.consumed_resource.recharge_die = obj.recharge_die ? obj.recharge_die : 6;
        this.consumed_resource.recharge_on = obj.recharge_on ? obj.recharge_on : [];
      }
      if (this.consumed_resource.resource_consumed === "Recharge" && this.consumed_resource.recharge_on.length === 0) {
        if (this.name.includes("(Recharge 5-6)")) {
          this.consumed_resource.recharge_on = [5,6];
          this.consumed_resource.resource_consumed = "Recharge";
        } else if (this.name.includes("(Recharge 4-6)")) {
          this.consumed_resource.recharge_on = [4,5,6];
          this.consumed_resource.resource_consumed = "Recharge";
        } else if (this.name.includes("(Recharge 6)")) {
          this.consumed_resource.recharge_on = [6];
          this.consumed_resource.resource_consumed = "Recharge";
        }
      }
      // if (this.description.includes("half as much")) {
      //   for (const effect of this.effects) {
      //     if (effect.type === "Damage") {
      //       effect.half_on_success = true;
      //     }
      //   }
      // }
    }
    this.recalc_damage_string();
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.saving_throw_ability_score = obj.saving_throw_ability_score ? obj.saving_throw_ability_score : "";
  //     if (obj.effects && obj.effects.length) {
  //       for (const effect of obj.effects) {
  //         this.effects.push(new AbilityEffect(effect)); // , this.name === "Proboscis"));
  //       }
  //       // if (this.name === "Proboscis") {
  //       //   // debugger;
  //       //   console.log(this.effects.map(o => o.clone()));
  //       // }
  //     } else if (obj.effect) {
  //       this.effects.push(new AbilityEffect(obj.effect));
  //     }
  //     this.range = obj.range ? obj.range : "";
  //     this.range_2 = obj.range_2 ? obj.range_2 : "";
  //     this.concentration = obj.concentration ? obj.concentration : false;
  //     this.notes = obj.notes ? obj.notes : "";
  //     this.duration = obj.duration ? obj.duration : "Instantaneous";
  //     this.components = obj.components ? [...obj.components] : [];
  //     this.material_component = obj.material_component ? obj.material_component : "";
  //     this.casting_time = obj.casting_time ? obj.casting_time : "A";
      
  //     this.consumed_resource = obj.consumed_resource ? new ConsumedResource(obj.consumed_resource) : new ConsumedResource();
  //     if (!obj.consumed_resource) {
  //       this.consumed_resource.resource_consumed = obj.resource_consumed ? obj.resource_consumed : "None";
  //       this.consumed_resource.amount_consumed = obj.amount_consumed ? +obj.amount_consumed : 0;
  //       this.consumed_resource.special_resource_amount = obj.special_resource_amount ? new UpgradableNumber(obj.special_resource_amount) : new UpgradableNumber();
  //       this.consumed_resource.special_resource_refresh_rule = obj.special_resource_refresh_rule ? obj.special_resource_refresh_rule : "";
  //     }

  //     this.attack_bonus = obj.attack_bonus ? new UpgradableNumber(obj.attack_bonus) : new UpgradableNumber();
  //     this.dc = obj.dc ? new UpgradableNumber(obj.dc) : new UpgradableNumber();
  //     if (this.saving_throw_ability_score !== "") {
  //       for (const effect of this.effects.filter(o => o.attack_type === "Save" && o.saving_throw_ability_score === "")) {
  //         effect.saving_throw_ability_score = this.saving_throw_ability_score;
  //       }
  //     }
  //     const desc_lower = this.description.toLowerCase();
  //     if (desc_lower.includes("melee") && this.effects.length > 0 && this.effects.filter(o => o.attack_type !== "None").length === 0) {
  //       // Melee Weapon Attack: +14 to hit, reach 10 ft., one target. Hit: 19 (2d10 + 8) piercing damage.
  //       const effect = this.effects[0];
  //       effect.attack_type = desc_lower.includes("spell") ? "Melee Spell" : "Melee";
  //       if (desc_lower.toLowerCase().includes(" reach ")) {
  //         let pieces = desc_lower.split(" reach ");
  //         pieces = pieces[1].split(" ");
  //         if (!isNaN(+pieces[0])) {
  //           effect.target_int.range.base = +pieces[0];
  //         }
  //       }
  //     } else if (desc_lower.includes("ranged weapon") && this.effects.length > 0 && (this.effects.filter(o => o.attack_type !== "None").length === 0 || this.effects.filter(o => o.attack_type !== "Ranged" && o.target_int.range.isBlank()).length === 0)) {
  //       // Ranged Weapon Attack: +{feature_source.non_magic_attack} to hit, range 80 ft./320 ft., one target. Hit: 5 (1d8 + {feature_source.non_magic_dmg_bonus}) piercing damage.
  //       // Ranged Weapon Attack: +4 to hit, range 150/600 ft., one target. Hit: 6 (1d8 + 2) piercing damage.
  //       // debugger;
  //       const effect = this.effects[0];
  //       effect.attack_type = "Ranged";
  //       if (desc_lower.toLowerCase().includes(" range ")) {
  //         let pieces = desc_lower.split(" range ");
  //         pieces = pieces[1].split(" ");
  //         if (pieces[0].includes("/")) {
  //           pieces = pieces[0].split("/");
  //           if (!isNaN(+pieces[0])) {
  //             effect.target_int.range.base = +pieces[0];
  //           }
  //           if (!isNaN(+pieces[1])) {
  //             effect.target_int.range2.base = +pieces[1];
  //           }
  //         } else {
  //           if (!isNaN(+pieces[0])) {
  //             effect.target_int.range.base = +pieces[0];
  //           }
  //           if (pieces[1].includes("/")) {
  //             pieces = pieces[1].split("/");
  //             if (!isNaN(+pieces[1])) {
  //               effect.target_int.range2.base = +pieces[1];
  //             }
  //           }
  //         }
  //       }
  //     }
  //     this.special_casting_time = obj.special_casting_time ? obj.special_casting_time : "";
  //     if (!STANDARD_CASTING_TIMES.includes(this.casting_time)) {
  //       this.special_casting_time = this.casting_time;
  //       this.casting_time = "Special";
  //     }
  //     // if (obj.name && obj.name.includes("Tadpole")) {
  //     //   debugger;
  //     // }
  //     if (!obj.consumed_resource) {
  //       this.consumed_resource.recharge_die = obj.recharge_die ? obj.recharge_die : 6;
  //       this.consumed_resource.recharge_on = obj.recharge_on ? obj.recharge_on : [];
  //     }
  //     if (this.consumed_resource.resource_consumed === "Recharge" && this.consumed_resource.recharge_on.length === 0) {
  //       if (this.name.includes("(Recharge 5-6)")) {
  //         this.consumed_resource.recharge_on = [5,6];
  //         this.consumed_resource.resource_consumed = "Recharge";
  //       } else if (this.name.includes("(Recharge 4-6)")) {
  //         this.consumed_resource.recharge_on = [4,5,6];
  //         this.consumed_resource.resource_consumed = "Recharge";
  //       } else if (this.name.includes("(Recharge 6)")) {
  //         this.consumed_resource.recharge_on = [6];
  //         this.consumed_resource.resource_consumed = "Recharge";
  //       }
  //     }
  //     // if (this.description.includes("half as much")) {
  //     //   for (const effect of this.effects) {
  //     //     if (effect.type === "Damage") {
  //     //       effect.half_on_success = true;
  //     //     }
  //     //   }
  //     // }
  //   }
  //   this.recalc_damage_string();
  // }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      saving_throw_ability_score: this.saving_throw_ability_score,
      effects: this.effects.map(o => o.toDBObj()),
      range: this.range,
      range_2: this.range_2,
      concentration: this.concentration,
      notes: this.notes,
      duration: this.duration,
      components: this.components,
      material_component: this.material_component,
      casting_time: this.casting_time,
      special_casting_time: this.special_casting_time,
      consumed_resource: this.consumed_resource.toDBObj(),
      // resource_consumed: this.resource_consumed,
      // amount_consumed: this.amount_consumed,
      // special_resource_amount: this.special_resource_amount,
      // special_resource_refresh_rule: this.special_resource_refresh_rule,
      // recharge_die: this.recharge_die,
      // recharge_on: this.recharge_on,
      attack_bonus: this.attack_bonus.toDBObj(),
      dc: this.dc.toDBObj(),
    };
  }

  get effect() {
    if (this.effects.length > 0) {
      return this.effects[0];
    } else {
      return new AbilityEffect();
    }
  }
  
  get the_effect_types() {
    return this.effects.map(o => o.type);
  }
  
  get damage_types() {
    return this.effects.flatMap(o => o.damage_types);
  }

  get the_attack_types() {
    return this.effects.map(o => o.attack_type);
  }

  get the_utility_details(): string[] {
    return this.effects.filter(o => ["Control","Utility"].includes(o.type)).map(o => o.utility_detail);
  }

  get use_name(): string {
    let use_name = super.get_use_name();
    switch (this.casting_time) {
      case "BA":
        if (use_name.endsWith("- Bonus")) {
          use_name = use_name.substring(0, use_name.length - 7).trim();
        } else if (use_name.endsWith("Bonus")) {
          use_name = use_name.substring(0, use_name.length - 5).trim();
        }
        use_name = use_name + " (Bonus Action)";
      break;
      case "RA":
        if (use_name.endsWith("- Reaction")) {
          use_name = use_name.substring(0, use_name.length - 10).trim();
        } else if (use_name.endsWith("Reaction")) {
          use_name = use_name.substring(0, use_name.length - 8).trim();
        }
        use_name = use_name + " (Reaction)";
      break;
    }
    return use_name;
  }

  get resource_consumed() {
    return this.consumed_resource.resource_consumed;
  }

  copy(copyMe: CreatureFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof CreatureAbility) {
      this.safe_copy(copyMe);
    }
  }

  safe_copy(copyMe: CreatureAbility) {
    this.saving_throw_ability_score = copyMe.saving_throw_ability_score;
    this.effects = copyMe.effects.map(e => e.clone());
    this.range = copyMe.range;
    this.range_2 = copyMe.range_2;
    this.concentration = copyMe.concentration;
    this.notes = copyMe.notes;
    this.duration = copyMe.duration;
    this.components = [...copyMe.components];
    this.material_component = copyMe.material_component;
    this.casting_time = copyMe.casting_time;
    this.special_casting_time = copyMe.special_casting_time;
    this.consumed_resource = copyMe.consumed_resource.clone();
    // this.resource_consumed = copyMe.resource_consumed;
    // this.amount_consumed = copyMe.amount_consumed;
    // this.special_resource_amount = copyMe.special_resource_amount;
    // this.special_resource_refresh_rule = copyMe.special_resource_refresh_rule;
    // this.recharge_die = copyMe.recharge_die;
    // this.recharge_on = copyMe.recharge_on;
    this.attack_bonus = copyMe.attack_bonus.clone();
    this.dc = copyMe.dc.clone();
    this.recalc_damage_string();
  }

  clone(drop_ids = false): CreatureAbility {
    const new_obj = new CreatureAbility();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }

  recalc_damage_string() {
    this.damage_string = "";
    this.rolls = [];
    this.effects.forEach(effect => {
      effect.potences.forEach(potence => {
        this.damage_string += potence.rolls.basic_string + " ";
        const roll = new RollPlus();
        roll.count = potence.rolls.count.base;
        roll.size = potence.rolls.size.base;
        roll.flat = potence.rolls.flat.base;
        roll.type = effect.type === "Damage" && effect.damage_types.length === 1 ? effect.damage_types[0] : effect.type;
        this.rolls.push(roll);
      });
      const bonus = effect.bonus.base; // For Creatures, this is the only bonus it can have
      if (bonus !== 0) {
        this.damage_string += `${bonus} `;
      }
      this.damage_string += effect.type === "Damage" && effect.damage_types.length === 1 ? `{${effect.damage_types[0]}` : `{${effect.type}} `;
    });
  }
  
  get_potence_string(
    obj: BasicCreatureInstance | SummonInstance | TransformInstance | ComplexCreatureInstance,
    effect: AbilityEffect, 
    log_it = false): string {
    let damage_string = "";
    const rolls: RollPlus[] = [];
    effect.potences.forEach(potence => {
      // damage_string += potence.rolls. + " ";
      const roll = new RollPlus();
      roll.count = char_util.value(potence.rolls.count, { obj }); // , log_it });
      roll.size = char_util.value(potence.rolls.size, { obj }); // , log_it });
      roll.flat = char_util.value(potence.rolls.flat, { obj, log_it });
      roll.type = effect.type === "Damage" && effect.damage_types.length === 1 ? effect.damage_types[0] : effect.type;
      if (log_it) {
        console.log(damage_string);
        console.log(roll);
        // debugger;
      }
      roll.recalculate_string();
      damage_string += roll.as_string;
      if (log_it) {
        console.log(damage_string);
        // debugger;
      }
      rolls.push(roll);
    });
    const bonus = effect.bonus.base; // For Creatures, this is the only bonus it can have
    if (bonus !== 0) {
      damage_string += `${bonus} `;
    }
    this.damage_string += effect.type === "Damage" && effect.damage_types.length === 1 ? `{${effect.damage_types[0]}` : `{${effect.type}} `;
    return damage_string;
  }
}

export class CreatureSpellAsAbility extends CreatureFeature {
  _name = "";
  spell_id = "";
  attack_bonus = 0;
  dc = 0;
  spell: Spell | null = null;
  spellcasting_ability = "";
  cast_at_level: SpellLevel = -1;
  resource_consumed = ""; // Special or Legendary Action
  amount_consumed = 0;
  special_resource_amount = 0;
  special_resource_refresh_rule = ""; // Short Rest, Long Rest, Dawn, 1 Hour, 8 Hours, 24 Hours
  
  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.spell_id = obj.spell_id ? obj.spell_id : "";
      this.attack_bonus = obj.attack_bonus ? obj.attack_bonus : 0;
      this.dc = obj.dc ? obj.dc : 0;
      this.spellcasting_ability = obj.spellcasting_ability ? obj.spellcasting_ability : "";
      this.cast_at_level = obj.cast_at_level ? obj.cast_at_level : -1;
      this.resource_consumed = obj.resource_consumed ? obj.resource_consumed : "";
      this.amount_consumed = obj.amount_consumed ? obj.amount_consumed : 0;
      this.special_resource_amount = obj.special_resource_amount ? obj.special_resource_amount : 0;
      this.special_resource_refresh_rule = obj.special_resource_refresh_rule ? obj.special_resource_refresh_rule : "";
    }
  }

  // load(obj: any): void {
  //   super.load(obj);
  //   if (obj) {
  //     this.spell_id = obj.spell_id ? obj.spell_id : "";
  //     this.attack_bonus = obj.attack_bonus ? obj.attack_bonus : 0;
  //     this.dc = obj.dc ? obj.dc : 0;
  //     this.spellcasting_ability = obj.spellcasting_ability ? obj.spellcasting_ability : "";
  //     this.cast_at_level = obj.cast_at_level ? obj.cast_at_level : -1;
  //     this.resource_consumed = obj.resource_consumed ? obj.resource_consumed : "";
  //     this.amount_consumed = obj.amount_consumed ? obj.amount_consumed : 0;
  //     this.special_resource_amount = obj.special_resource_amount ? obj.special_resource_amount : 0;
  //     this.special_resource_refresh_rule = obj.special_resource_refresh_rule ? obj.special_resource_refresh_rule : "";
  //   }
  // }
  
  get the_effect_types(): EffectType[] {
    return this.spell ? this.spell.effect_types : [];
  }
  
  get damage_types() {
    return this.spell ? this.spell.effects.flatMap(o => o.damage_types) : [];
  }

  get the_attack_types(): AttackType[] {
    return this.spell ? this.spell.attack_types : [];
  }

  get the_utility_details(): string[] {
    return this.spell ? this.spell.the_utility_details : [];
  }

  get concentration(): boolean {
    if (this.spell) {
      return this.spell.concentration;
    }
    return false;
  }

  get level() {
    if (this.cast_at_level > -1) {
      return this.cast_at_level;
    } else if (this.spell) {
      return this.spell.level;
    }
    return 0;
  }

  get casting_time() {
    if (this.spell) {
      return this.spell.casting_time;
    }
    return "A";
  }

  get special_casting_time(): string {
    if (this.spell) {
      return this.spell.special_casting_time;
    }
    return "";
  }

  toDBObj(): any {
    const db_obj = super.toDBObj();
    return {
      ...db_obj,
      spell_id: this.spell_id,
      attack_bonus: this.attack_bonus,
      dc: this.dc,
      spellcasting_ability: this.spellcasting_ability,
      cast_at_level: this.cast_at_level,
      resource_consumed: this.resource_consumed,
      amount_consumed: this.amount_consumed,
      special_resource_amount: this.special_resource_amount,
      special_resource_refresh_rule: this.special_resource_refresh_rule,
    };
  }

  copy(copyMe: CreatureFeature, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    if (copyMe instanceof CreatureSpellAsAbility) {
      this.safe_copy(copyMe);
    } 
  }

  safe_copy(copyMe: CreatureSpellAsAbility) {
    this._name = copyMe._name;
    this.spell_id = copyMe.spell_id;
    this.attack_bonus = copyMe.attack_bonus;
    this.dc = copyMe.dc;
    this.spellcasting_ability = copyMe.spellcasting_ability;
    this.cast_at_level = copyMe.cast_at_level;
    this.resource_consumed = copyMe.resource_consumed;
    this.amount_consumed = copyMe.amount_consumed;
    this.special_resource_amount = copyMe.special_resource_amount;
    this.special_resource_refresh_rule = copyMe.special_resource_refresh_rule;
    this.spell = copyMe.spell;
  }

  clone(drop_ids = false): CreatureSpellAsAbility {
    const new_obj = new CreatureSpellAsAbility();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }

  connectSpell(spell: Spell) {
    this.spell = spell;
  }

  get_potence_string(char: BasicCreatureInstance | SummonInstance | TransformInstance, 
    effect: AbilityEffect, 
    log_it = false): string {
    let potence_string = "";
    const the_effect = effect; // ? effect : this.effect;
    // log_it = this.name.includes("Guidance");
    const slot_level = this.level;
    if (the_effect.type === "Self Condition") {
      potence_string = "Apply";
    } else {
      const potence_rolls = this.get_potence_rolls(slot_level, char, effect, log_it);
      potence_rolls.forEach(rolls => {
        const rolls_string = rolls.as_string;
        if (rolls_string.startsWith("-")) {
          potence_string += rolls_string;
        } else if (potence_string.length > 0) {
          potence_string += "+" + rolls_string;
        } else {
          potence_string = rolls_string;
        }
      });
    }
    return potence_string;
  }

  get_potence_rolls(slot_level: number, 
    char: BasicCreatureInstance | SummonInstance | TransformInstance, 
    effect: AbilityEffect, 
    log_it = false): RollPlus[] {
    const roll_pluses: RollPlus[] = [];
    const the_effect = effect; // ? effect : this.effect;
    let bonuses = the_effect ? char_util.value(the_effect.bonus, { 
      base_slot_level: this.level, 
      slot_level, 
      log_it 
    }) : 0;
    // log_it = this.name === "Aura of Life";
    if (the_effect.type === "Self Condition") {
      return []; // Not needed
    } else {
      const potence = this.get_potence(slot_level, char, the_effect);
      if (log_it) {
        // debugger;
        console.log(this);
        console.log(the_effect);
        console.log(potence);
        if (potence) {
          console.log(potence.rolls);
        }
      }
      const ability_scores = char.mind.current_ability_scores;
      if (potence) {
        // Removing this because for Armor of Agathys it's causing it to be 5+5 instead of just 5
        // I don't think there's anything that needs this now, 
        // but I'll keep it here so that if I need to refactor later I can.
        // bonuses += potence.rolls.flat.value({ 
        //   char, 
        //   source_id: this.source_id, 
        //   base_slot_level: this.level, 
        //   slot_level, 
        //   log_it 
        // });
        const potence_rolls = char_util.toRollPlus(potence.rolls, { 
          base_slot_level: this.level, slot_level, log_it 
        });
        potence_rolls.recalculate_string();
        if (log_it) {
          console.log(potence_rolls);
          console.log(potence_rolls.as_string);
        }
        roll_pluses.push(potence_rolls);
        roll_pluses.filter(o => o.ability_score !== "").forEach(rolls => {
          const mod = ability_scores.getModifier(rolls.ability_score);
          if (mod) {
            bonuses += mod;
          }
          rolls.ability_score = "";
        });
        if (bonuses !== 0) {
          const rolls = new RollPlus();
          rolls.flat = bonuses;
          rolls.recalculate_string();
          roll_pluses.push(rolls);
        }
      }
    }
    return roll_pluses;
  }

  get_potence(slot_level: number, 
    char: BasicCreatureInstance | SummonInstance | TransformInstance, 
    effect: AbilityEffect): Potence | null {
    if (this.spell) {
      const the_effect = effect; // ? effect : this.the_spell.effect;
      if (!["Control","Utility"].includes(the_effect.type)) {
        let level = -1;
        let potence_type = the_effect.potence_type;
        if (slot_level === 0 && potence_type === "Slot") {
          // It's a cantrip, it should actually be Character
          potence_type = "Character";
        }
        if (effect.potences.length === 1) {
          level = effect.potences[0].level;
        } else if (potence_type === "Slot") {
          level = slot_level;
        } else if (potence_type === "Character") {
          level = char.challenge_rating;
        } else if (potence_type === "Class") {
          level = char.challenge_rating;
        }
        let potence_finder: Potence[] = [];
        if (level === -1 && the_effect.potences.length === 1) {
          potence_finder = the_effect.potences;
        } else {
          potence_finder = the_effect.potences.filter(o => o.level === level);
        }
        if (potence_finder.length === 0) {
          potence_finder = the_effect.potences.filter(o => o.level < level);
          if (potence_finder.length > 0) {
            const max_level = Math.max(...potence_finder.map(o => { return o.level }));
            potence_finder = the_effect.potences.filter(o => o.level === max_level);
          }
        } 
        if (potence_finder.length === 1) {
          const potence = new Potence(potence_finder[0]);
          potence.rolls.ability_score = "";
          return potence;
        }
      }
    }
    return null;
  }
}